import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, resolveUrl } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { TagInput } from "@/components/admin/TagInput";
import { Badge, Field, PageTitle, btnDanger, btnPrimary, btnSecondary, btnSmall, card, inputCls, linkCls, selectCls } from "@/components/admin/ui";

const TAGS = ["Performance", "Competition", "Festival"];
const VISIBILITY = [
  { value: "public", label: "Public", desc: "Visible on the site and in search" },
  { value: "unlisted", label: "Unlisted", desc: "Only viewable with the link" },
  { value: "private", label: "Private", desc: "Only logged-in admins can view" },
];

const Check = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 text-[14px] cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 accent-[#B644DC]" data-testid={testId} />
    {label}
  </label>
);

const toggle = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

const ThumbnailPanel = ({ video, form, setForm, bunny, onUploaded }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/videos/${video.id}/thumbnail-file`, fd);
      setForm((f) => ({ ...f, thumbnail_url: data.thumbnail_url }));
      onUploaded?.();
      toast.success("Custom thumbnail uploaded");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const stills = bunny?.stills || [];
  return (
    <div className={`${card} p-4`} data-testid="thumbnail-panel">
      <p className="text-[13px] font-semibold mb-2">Thumbnail</p>
      <div className="aspect-video rounded bg-[#E5E7EB] overflow-hidden">
        {form.thumbnail_url && <img src={resolveUrl(form.thumbnail_url)} alt="" className="w-full h-full object-cover" data-testid="thumbnail-preview" />}
      </div>
      {video.source_type === "bunny" && (
        <>
          <p className="mt-3 text-[12px] text-[#6B7280]">{stills.length ? "Choose a still from the video:" : video.status === "ready" ? "No stills available yet." : "Stills appear once processing finishes."}</p>
          {stills.length > 0 && (
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {stills.map((s) => (
                <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, thumbnail_url: s }))} className={`aspect-video rounded overflow-hidden border-2 ${form.thumbnail_url === s ? "border-[#B644DC]" : "border-transparent hover:border-[#D1D5DB]"}`} data-testid="thumbnail-still">
                  <img src={s} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </>
      )}
      <div className="mt-3 flex gap-2">
        <button type="button" className={btnSmall} onClick={() => fileRef.current?.click()} disabled={busy} data-testid="custom-thumbnail-button">
          {busy ? "Uploading…" : "Upload custom thumbnail"}
        </button>
        {form.thumbnail_url !== video.default_thumbnail_url && (
          <button type="button" className={btnSmall} onClick={() => setForm((f) => ({ ...f, thumbnail_url: video.default_thumbnail_url }))} data-testid="default-thumbnail-button">
            Use default
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
};

export default function AdminVideoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: video, reload, setData, error } = useApi(`/admin/videos/${id}`);
  const { data: cats } = useApi("/admin/categories");
  const { data: styles, reload: reloadStyles } = useApi("/admin/dance-styles");
  const { data: festivals } = useApi("/admin/festivals");
  const [form, setForm] = useState(null);
  const [bunny, setBunny] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (video && !form) {
      setForm({ title: video.title, tags: video.tags || [], category_ids: video.category_ids || [], festival_id: video.festival_id || "", visibility: video.visibility, dance_style_ids: video.dance_style_ids || [], thumbnail_url: video.thumbnail_url });
    }
  }, [video, form]);

  useEffect(() => {
    if (!video || video.source_type !== "bunny") return;
    const poll = () => api.get(`/admin/videos/${id}/bunny`).then((r) => { setBunny(r.data); setData((v) => (v ? { ...v, status: r.data.status, encode_progress: r.data.encode_progress } : v)); }).catch(() => {});
    poll();
    if (video.status === "ready") return;
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, video?.source_type, video?.status]);

  if (error)
    return (
      <div data-testid="video-not-found">
        <p className="text-[15px] font-semibold">Video not found</p>
        <Link to="/admin/videos" className={`text-[13px] ${linkCls}`}>← Back to videos</Link>
      </div>
    );
  if (!video || !form) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/videos/${id}`, { ...form, festival_id: form.festival_id || null });
      setData(data);
      toast.success("Video saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${video.title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/videos/${id}`);
      toast.success("Video deleted");
      navigate("/admin/videos");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createStyle = async (name) => {
    const { data } = await api.post("/admin/dance-styles", { name });
    await reloadStyles();
    return data;
  };

  return (
    <div data-testid="admin-video-detail-page">
      <p className="text-[13px] mb-3"><Link to="/admin/videos" className={linkCls} data-testid="back-to-videos">← Videos</Link></p>
      <PageTitle title="Video details">
        <Link to={`/watch/${id}`} target="_blank" className={btnSecondary} data-testid="open-watch-page">Open watch page</Link>
        <button type="button" className={btnPrimary} onClick={save} disabled={saving} data-testid="save-video-button">{saving ? "Saving…" : "Save changes"}</button>
      </PageTitle>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div className="space-y-5">
          <div className={`${card} p-5 space-y-5`}>
            <Field label="Title">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="video-title-input" />
            </Field>
            <Field label="Shows on" hint="Each page lists the videos tagged with it: Performances, Competitions, Festivals.">
              <div className="flex gap-6 mt-1">
                {TAGS.map((t) => <Check key={t} label={t} checked={form.tags.includes(t)} onChange={() => setForm({ ...form, tags: toggle(form.tags, t) })} testId={`tag-checkbox-${t.toLowerCase()}`} />)}
              </div>
            </Field>
            <Field label="Categories (home page rows)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                {cats?.map((c) => <Check key={c.id} label={c.name} checked={form.category_ids.includes(c.id)} onChange={() => setForm({ ...form, category_ids: toggle(form.category_ids, c.id) })} testId={`category-checkbox-${c.id}`} />)}
                {cats && cats.length === 0 && <p className="text-[12px] text-[#6B7280]">No categories yet — <Link to="/admin/categories" className={linkCls}>create one</Link>.</p>}
              </div>
            </Field>
            <Field label="Festival" hint="Applies that festival's full template (sales letter + passes) to this watch page.">
              <select value={form.festival_id} onChange={(e) => setForm({ ...form, festival_id: e.target.value })} className={selectCls} data-testid="festival-select">
                <option value="">No festival</option>
                {festivals?.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </Field>
            <Field label="Dance style">
              <TagInput value={form.dance_style_ids} options={styles || []} onChange={(v) => setForm({ ...form, dance_style_ids: v })} onCreate={createStyle} placeholder="Search dance styles…" testId="dance-style-tags" />
            </Field>
            <Field label="Visibility">
              <div className="space-y-2 mt-1">
                {VISIBILITY.map((o) => (
                  <label key={o.value} className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="visibility" value={o.value} checked={form.visibility === o.value} onChange={() => setForm({ ...form, visibility: o.value })} className="mt-1 accent-[#B644DC]" data-testid={`visibility-${o.value}`} />
                    <span className="text-[14px]"><span className="font-semibold">{o.label}</span> <span className="text-[#6B7280]">— {o.desc}</span></span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <div className={`${card} p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
            <div>
              <p className="text-[13px] font-semibold">Danger zone</p>
              <p className="text-[12px] text-[#6B7280]">Deletes this video{video.source_type === "bunny" ? " and its uploaded file" : ""}.</p>
            </div>
            <button type="button" className={btnDanger} onClick={remove} data-testid="delete-video-button">Delete video</button>
          </div>
        </div>

        <div className="space-y-5">
          <ThumbnailPanel video={video} form={form} setForm={setForm} bunny={bunny} onUploaded={reload} />
        </div>
      </div>
    </div>
  );
}
